import type { RefObject } from 'preact';
import { useCallback, useRef } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { Keys, addKeys } from '../utils/UNSAFE_keys';
import { DataState, Metadata, Range, SelectionDetail } from '../UNSAFE_Collection';
import {
  SelectionRangeDetail,
  useSelection,
  useAdditiveSelection
} from '../hooks/PRIVATE_useSelection';
import { useItemAction } from '../hooks/PRIVATE_useItemAction';
import { Props as ListViewProps } from '../UNSAFE_ListView';
import { getPrevNextKey, findElementByKey, keyExtractor } from '../utils/PRIVATE_collectionUtils';
import { scrollToVisible, LIST_ITEM_SELECTOR } from './ListUtils';

export const useSelectionItemAction = <K extends string | number, D>(
  currentKey: K | undefined,
  selectedKeys: Keys<K>,
  selectionMode: ListViewProps<K, D>['selectionMode'] = 'none',
  onSelectionChange: ListViewProps<K, D>['onSelectionChange'],
  onItemAction: ListViewProps<K, D>['onItemAction'],
  data: ListViewProps<K, D>['data'],
  viewportConfig: ListViewProps<K, D>['viewportConfig'],
  scrollToVisibleOffset: ListViewProps<K, D>['scrollToVisibleOffset'],
  rootRef: RefObject<HTMLDivElement>,
  pendingSelectionCallback: (detail: SelectionRangeDetail<K>, value: Range) => void,
  isEmbedded: boolean
) => {
  // tracking the anchor key which is used for shift+click selection
  const anchorKey = useRef<K | undefined>();

  const handleSelectionChange = useCallback(
    (detail: SelectionDetail<K>) => {
      if (onSelectionChange) {
        if (detail.value.all === false && detail.value.keys.size > 0) {
          // during regular selection, the last key selected by a user gesture is the anchor key
          anchorKey.current = Array.from(detail.value.keys.values()).pop();
        }
        onSelectionChange(detail);
      }
    },
    [anchorKey, onSelectionChange]
  );

  const handleSelectionRangeChange = useCallback(
    (detail: SelectionRangeDetail<K>) => {
      if (data && onSelectionChange) {
        const value = handleSelectionRange(detail, data);
        if (Array.isArray(value)) {
          if (detail.eventType === 'shiftSpace') {
            // during range selection by shift+space, the first key selected by a user gesture is the anchor key
            anchorKey.current = detail.value.start;
          } else {
            // during range selection by shift+click/arrow keys, the last key selected by a user gesture is the anchor key
            anchorKey.current = detail.value.end;
          }
          if (isEmbedded || selectionMode === 'multipleToggle') {
            const keys = addKeys(selectedKeys, value);
            onSelectionChange({ value: keys, target: null });
          } else {
            onSelectionChange({ value: { all: false, keys: new Set(value) }, target: null });
          }
        } else {
          pendingSelectionCallback(detail, value);
        }
      }
    },
    [data, isEmbedded, selectedKeys, onSelectionChange, pendingSelectionCallback]
  );

  const { selectionProps } = useSelection(
    (element: HTMLElement) => {
      if (element === rootRef.current) {
        return currentKey === undefined ? null : currentKey;
      }
      return keyExtractor(element, LIST_ITEM_SELECTOR) as K;
    },
    selectedKeys,
    isEmbedded || selectionMode === 'multipleToggle' ? 'none' : selectionMode,
    false,
    'replace',
    handleSelectionChange,
    anchorKey.current,
    currentKey,
    (currentKey: K | undefined, isPrev: boolean) =>
      getPrevNextKey(rootRef.current, currentKey, isPrev, LIST_ITEM_SELECTOR),
    undefined,
    (key: K) => {
      if (rootRef.current) {
        const elem = findElementByKey(rootRef.current, key, LIST_ITEM_SELECTOR);
        if (elem) {
          const scroller = viewportConfig?.scrollerRef.current;
          const offset = scrollToVisibleOffset ? scrollToVisibleOffset(rootRef.current) : 0;
          scrollToVisible(elem, scroller != null ? scroller : rootRef.current, offset);
        }
      }
    },
    handleSelectionRangeChange
  );

  const { eventProps: additiveSelectionProps } = useAdditiveSelection(
    (element: HTMLElement) => {
      if (element === rootRef.current) {
        return currentKey === undefined ? null : currentKey;
      }
      return keyExtractor(element, LIST_ITEM_SELECTOR) as K;
    },
    selectedKeys,
    selectionMode === 'multipleToggle' ? 'multiple' : isEmbedded ? selectionMode : 'none',
    false,
    (key: K) => {
      if (data) {
        return data.data.findIndex(
          (value: { data: D; metadata: Metadata<K> }) => value.metadata.key === key
        );
      }
      return -1;
    },
    (startIndex: number, endIndex: number) => {
      if (data) {
        const keys = data.data.map(
          (value: { data: D; metadata: Metadata<K> }) => value.metadata.key
        );
        return keys.slice(startIndex, endIndex);
      }
      return [];
    },
    (currentKey: K | undefined, isPrev: boolean) =>
      getPrevNextKey(rootRef.current, currentKey, isPrev, LIST_ITEM_SELECTOR),
    currentKey,
    handleSelectionChange,
    handleSelectionRangeChange,
    selectionMode === 'multipleToggle' ? false : isEmbedded
  );

  const itemActionProps = useItemAction(
    currentKey,
    data,
    onItemAction,
    LIST_ITEM_SELECTOR,
    isEmbedded
  );

  return {
    eventProps: mergeProps(selectionProps, itemActionProps, additiveSelectionProps),
    handleSelectionChange
  };
};

export const handleSelectionRange = <K, D>(
  detail: SelectionRangeDetail<K>,
  dataState: DataState<K, D>
) => {
  const keys = dataState.data.map((value: { data: D; metadata: Metadata<K> }) => {
    return value.metadata.key;
  });
  const startIndex = keys.indexOf(detail.value.start);
  const endIndex = keys.indexOf(detail.value.end);
  const minIndex = Math.min(startIndex, endIndex);
  const maxIndex = Math.max(startIndex, endIndex);
  if (minIndex === -1) {
    // return a range to fetch so we can find all the keys, maxIndex should not be -1
    return { offset: 0, count: dataState.totalSize };
  } else {
    // range of keys are in the current viewport, return them
    return keys.slice(minIndex, maxIndex + 1);
  }
};
